import { useEffect, useState } from "react";
import { TrashBinMinimalistic } from "@solar-icons/react/ssr";
import {
    Autocomplete,
    Button,
    Grid,
    IconButton,
    Modal,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    useTheme,
    useMediaQuery,
    Typography,
} from "@mui/material";
import { request } from "@/Api";
import { CustomBox } from "@/common/StyledComponents";
import { getCurrencySymbol } from "@/utils";
import {
    getClientCurrency,
    getLineGroup,
    getReadOnly,
    store,
} from "./bidStore";

export default function AddonForm({
    bid: paramBid,
    addonData,
    onAddonChange,
    onAddonBlur,
    onAddonDelete,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const activeBid = paramBid || store.bid;

    const [addon, setAddon] = useState();

    const lineGroup =
        addon &&
        addon.line_group_id &&
        (activeBid.line_groups || []).find((g) => g.id === addon.line_group_id);
    const readOnly = getReadOnly();
    let defaultAmount = addon && addon.amount;
    defaultAmount =
        defaultAmount == null
            ? 0
            : addon.type == "amount"
              ? addon.amount
              : addon.amount * 100;
    defaultAmount = parseFloat(defaultAmount).toFixed(2);
    const clientCurrency = getClientCurrency();
    const currencySymbol = getCurrencySymbol(clientCurrency);

    const lineGroupType = lineGroup
        ? lineGroup.parent_id
            ? "category"
            : "section"
        : "bid";
    const lineGroupText = `${lineGroupType}: ${lineGroup ? lineGroup.name : activeBid.name}`;

    const handleAddonBlur = (field) => (e) => {
        onAddonBlur && onAddonBlur(field, e.target.value);
    };

    const handleInputChange = (field) => (e) => {
        setAddon({ ...addon, [field]: e.target.value });
        onAddonChange && onAddonChange(field, e.target.value);
    };

    const handleAmountChange = (e) => {
        const val = e.target.value;
        if (!/^\d*\.?\d*$/.test(val)) {
            // if not decimal input
            return;
        } else if (val.includes(".") && val.split(".")[1].length > 2) {
            return;
        }
        setAddon({ ...addon, amount: val });
        if (val != "" && !val.endsWith(".")) {
            const valCalculated =
                addon.type == "amount"
                    ? val
                    : (parseFloat(val) / 100).toString();
            onAddonChange && onAddonChange("amount", valCalculated);
        }
    };

    const handleAmountBlur = (e) => {
        const val = e.target.value;
        if (!/^\d*\.?\d*$/.test(val)) {
            // if not decimal input
            return;
        }
        if (val != "" && !val.endsWith(".")) {
            const valCalculated =
                addon.type == "amount"
                    ? val
                    : (parseFloat(val) / 100).toString();
            onAddonBlur && onAddonBlur("amount", valCalculated);
        }
    };

    const handleToggleButtonChange = (field) => (e, newValue) => {
        if (addon[field] == newValue || !newValue) {
            return;
        }
        setAddon({ ...addon, [field]: newValue });
        onAddonChange && onAddonChange(field, newValue);
        onAddonBlur && onAddonBlur(field, newValue);
    };

    const handleAddonDelete = () => {
        onAddonDelete && onAddonDelete(addon.id);
    };

    useEffect(() => {
        let parsedAmount = addonData.amount;
        if (addonData.type == "percentage") {
            parsedAmount =
                Math.round(
                    (parseFloat(addonData.amount) + Number.EPSILON) * 1000000,
                ) / 10000;
        }
        setAddon({ ...addonData, amount: parsedAmount });
    }, [addonData]);

    if (!addon) {
        return;
    }
    return (
        <div>
            <Grid
                container
                justifyContent={"space-between"}
                wrap="nowrap"
                spacing={1}
                alignItems={"center"}
                paddingBottom={2}
            >
                <Grid item flexGrow={1} flexBasis={1000}>
                    <TextField
                        disabled={readOnly}
                        fullWidth
                        size="small"
                        placeholder="Name..."
                        value={addon.name || ""}
                        onInput={handleInputChange("name")}
                        onBlur={handleAddonBlur("name")}
                    />
                </Grid>
                {lineGroupType != "bid" && (
                    <Grid item minWidth={180}>
                        <TextField
                            size="small"
                            placeholder="0"
                            value={lineGroupText}
                            disabled
                            onInput={handleInputChange("name")}
                            onBlur={handleAddonBlur("name")}
                        />
                    </Grid>
                )}
                <Grid item minWidth={90}>
                    <ToggleButtonGroup
                        disabled={readOnly}
                        color="success"
                        size="small"
                        value={addon.type || "amount"}
                        exclusive
                        onChange={handleToggleButtonChange("type")}
                    >
                        <ToggleButton value={"amount"}>
                            <div style={{ paddingLeft: 8, paddingRight: 8 }}>
                                $
                            </div>
                        </ToggleButton>
                        <ToggleButton value={"percentage"}>
                            <div style={{ paddingLeft: 8, paddingRight: 8 }}>
                                %
                            </div>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Grid>
                <Grid
                    item
                    flexGrow={1}
                    flexBasis={100}
                    minWidth={isMobile ? 80 : 180}
                >
                    <Tooltip
                        title={`${clientCurrency}${addonData.client_quoted}`}
                    >
                        <TextField
                            disabled={readOnly}
                            type="text"
                            size="small"
                            placeholder="0"
                            value={addon.amount || ""}
                            onInput={handleAmountChange}
                            onBlur={handleAmountBlur}
                            inputProps={{ style: { textAlign: "right" } }}
                            InputProps={{
                                startAdornment: addon.type == "amount" && (
                                    <span style={{ paddingRight: 10 }}>
                                        {" "}
                                        ${" "}
                                    </span>
                                ),
                                endAdornment: addon.type == "percentage" && (
                                    <span style={{ paddingLeft: 10 }}> % </span>
                                ),
                            }}
                        />
                    </Tooltip>
                </Grid>
                <Grid item maxWidth={50}>
                    <IconButton disabled={readOnly} onClick={handleAddonDelete}>
                        <TrashBinMinimalistic />
                    </IconButton>
                </Grid>
            </Grid>
        </div>
    );
}

export function AddonPopup({
    bid: paramBid,
    isOpen,
    close,
    lineGroupId = null,
    addonData = null,
}) {
    const activeBid = paramBid || store.bid;
    const bidId = activeBid.id;
    const theme = useTheme();
    const [addon, setAddon] = useState({});
    const handleConfirmAddon = () => {
        // TODO: take out the api request responsibility from this component, but the challenge is handling internal states by async functions from outside (perhaps consider using await)
        if (addon.id) {
            request.put(`bids/${bidId}/addon/${addon.id}`, addon).then((_) => {
                close();
            });
        } else {
            request.post(`/bids/${bidId}/addon`, addon).then((_) => {
                close();
            });
        }
    };
    const handleAddonChange = (field, value) => {
        setAddon({ ...addon, [field]: value });
    };
    useEffect(() => {
        setAddon({
            line_group_id: lineGroupId,
            bid_id: bidId,
            type: addon.type || "amount",
            ...addonData,
        });
    }, [lineGroupId, addonData, isOpen]);

    return (
        <Modal open={isOpen} keepMounted={false} onClose={close}>
            <CustomBox sx={{ background: theme.palette.background.default }}>
                <Typography variant="h2">{`${addon.id ? "Edit" : "Create a new"} add-on`}</Typography>

                <AddonForm
                    bid={paramBid}
                    addonData={addon}
                    onAddonChange={handleAddonChange}
                />
                <Grid container spacing={1} justifyContent={"right"}>
                    <Grid item>
                        <Button color="secondary" onClick={close}>
                            Cancel
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button onClick={handleConfirmAddon}>
                            {`${addon.id ? "Update" : "Create"} Add-on`}
                        </Button>
                    </Grid>
                </Grid>
            </CustomBox>
        </Modal>
    );
}
